import { reportPresentation } from "./reportPresentation";
import fontUrl from "./report-fonts/NotoSans-Regular.ttf";
// Load the maintained browser bundle as a local asset. The legacy app compiler cannot
// parse this dependency's newer syntax; no global webpack/Babel changes are needed.
// eslint-disable-next-line import/no-webpack-loader-syntax
import engineUrl from "!!file-loader!jspdf/dist/jspdf.umd.min.js";

// An explicit export allowlist, shared with the screen. No API calls or DTO serialization.
export function reportPdfBlocks(response, { request = "", originalSearch = "", generatedAt = new Date() } = {}) {
  const report = reportPresentation(response);
  const blocks = [];
  const add = (value, style = "body") => { if (value) blocks.push({ text: value, style }); };
  const notes = (heading, values) => { if (values.length) { add(heading, "heading"); values.forEach(value => add(value)); } };
  const findings = values => values.forEach(finding => {
    add(finding.label, "subheading"); add(finding.content);
    if (finding.citations.length) {
      finding.citations.forEach(citation => {
        add(`Source: ${citation.documentLabel}${citation.page ? ` | ${citation.pageLabel || `Physical page ${citation.page}`}` : ""}${citation.sourceNote ? ` | ${citation.sourceNote}` : ""}`, "source");
        // The finding is already the exact supporting span in most report responses.
        if (citation.quote && citation.quote.trim() !== finding.content.trim()) add(citation.quote, "source");
      });
    } else add("Citation unavailable for this finding.", "source");
    finding.references.filter(reference => !finding.citations.some(citation => citation.identity === reference.identity))
      .forEach(reference => add(`Source: ${reference.documentLabel}`, "source"));
  });
  add("DocumentSync AI Report", "title");
  add("Search Results Analysis", "subheading");
  add(`Generated ${generatedAt.toLocaleString()}`, "source");
  if (originalSearch) { add("Original search", "heading"); add(originalSearch); }
  if (request) { add("Report request", "heading"); add(request); }
  if (report.documentCount !== null) add(`Based on ${report.documentCount} current search ${report.documentCount === 1 ? "result" : "results"}`);
  if (report.analyzedCount !== null) add(`${report.analyzedCount} documents analyzed`, "source");
  add(report.scopeDescription, "source"); add(report.notice);
  if (report.executiveSummary) { add("Executive Summary", "heading"); add(report.executiveSummary); }
  // What the request asked across the documents comes first; the per-document evidence follows it.
  if (report.crossDocumentFindings.length) { add("Cross-Document Observations", "heading"); findings(report.crossDocumentFindings); }
  if (report.documents.length) add("Document Analysis", "heading");
  report.documents.forEach(document => {
    add(document.name, "subheading");
    add(document.notice); findings(document.findings); notes("Missing Information", document.missingInformation);
  });
  notes("Missing Information", report.missingInformation); notes("Limitations", report.limitations);
  if (report.documents.length) { add("Sources", "heading"); report.documents.forEach(document => add(document.name, "source")); }
  add(report.pageNavigationNote, "source");
  add(report.coverageNote, "source");
  return blocks;
}

// Paginate text rather than capturing a screenshot, so long reports remain selectable/readable.
export function createReportPdf(jsPDF, font, response, options) {
  const pdf = new jsPDF({ unit: "pt", format: "letter", compress: true, putOnlyUsedFonts: true });
  pdf.addFileToVFS("ReportSans.ttf", font);
  pdf.addFont("ReportSans.ttf", "ReportSans", "normal");
  pdf.setFont("ReportSans", "normal");
  const width = pdf.internal.pageSize.getWidth(), height = pdf.internal.pageSize.getHeight();
  const margin = 48, bottom = height - 54;
  let y = margin;
  reportPdfBlocks(response, options).forEach(block => {
    const size = { title: 22, heading: 14, subheading: 12, source: 9, body: 11 }[block.style];
    const leading = size * 1.5;
    pdf.setFontSize(size);
    pdf.setTextColor(block.style === "source" ? "#526477" : "#24364b");
    const lines = pdf.splitTextToSize(block.text, width - margin * 2);
    const heading = ["title", "heading", "subheading"].includes(block.style);
    if (y + leading * (heading ? Math.min(lines.length + 2, 5) : 1) > bottom) { pdf.addPage(); y = margin; }
    lines.forEach(line => {
      if (y + leading > bottom) { pdf.addPage(); y = margin; }
      pdf.text(line, margin, y + size); y += leading;
    });
    y += heading ? 8 : 10;
  });
  const total = pdf.getNumberOfPages();
  for (let page = 1; page <= total; page += 1) {
    pdf.setPage(page); pdf.setFontSize(8); pdf.setTextColor("#526477");
    pdf.text(`DocumentSync AI Report | Page ${page} of ${total}`, margin, height - 28);
  }
  return pdf;
}

let fontPromise;
function loadFont() {
  if (!fontPromise) fontPromise = fetch(fontUrl).then(response => {
    if (!response.ok) throw new Error("Report font unavailable");
    return response.arrayBuffer();
  }).then(buffer => {
    let binary = "";
    new Uint8Array(buffer).forEach(value => { binary += String.fromCharCode(value); });
    return btoa(binary);
  }).catch(error => { fontPromise = null; throw error; });
  return fontPromise;
}

let enginePromise;
function loadPdfEngine() {
  if (!enginePromise) enginePromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = engineUrl; script.async = true;
    script.onload = () => {
      if (window.jspdf && window.jspdf.jsPDF) resolve(window.jspdf.jsPDF);
      else { script.remove(); reject(new Error("PDF engine unavailable")); }
    };
    script.onerror = () => { script.remove(); reject(new Error("PDF engine unavailable")); };
    document.head.appendChild(script);
  }).catch(error => { enginePromise = null; throw error; });
  return enginePromise;
}

export async function downloadReportPdf(response, options) {
  // These load local application assets only; no search, report, RAG or summary request.
  const [jsPDF, font] = await Promise.all([loadPdfEngine(), loadFont()]);
  const pdf = createReportPdf(jsPDF, font, response, options);
  await pdf.save("DocumentSync-AI-Report.pdf", { returnPromise: true });
}
